<!-- Chat Panel -->
@if (isChatOpen()) {
  <div
    class="fixed z-40 flex flex-col rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800
           bottom-[10px] right-[10px] w-[calc(100%-20px)] h-[60vh]
           md:w-[380px] md:h-auto md:top-[82px] md:bottom-[10px]"
    role="dialog"
    aria-label="Support assistant"
  >
    <!-- Header -->
    <div class="flex flex-none items-center justify-between px-3 h-10 rounded-t-lg bg-black text-white">
      <div class="flex items-center space-x-2 min-w-0">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <span class="text-sm font-medium truncate">Assistant</span>
        @if (venueName(); as name) {
          <span class="text-xs text-gray-300 truncate">· {{ name }}</span>
        }
      </div>
      <div class="flex items-center space-x-1">
        <button
          (click)="newConversation()"
          [disabled]="isSending() || messages().length === 0"
          class="flex items-center justify-center h-6 w-6 rounded hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Start a new conversation"
          title="New conversation"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <button
          (click)="toggleChat()"
          class="flex items-center justify-center h-6 w-6 rounded hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          aria-label="Collapse chat"
          title="Collapse"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Chat Body -->
    <div #chatBody class="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 text-sm" aria-live="polite">
      <!-- Empty state -->
      @if (messages().length === 0) {
        <div class="flex flex-col items-center justify-center h-full text-center text-gray-400 dark:text-gray-500 space-y-1">
          <div>Ask where to find something or how an admin task works.</div>
          @if (venueName(); as name) {
            <div class="text-xs">Questions will be scoped to {{ name }}.</div>
          }
        </div>
      }

      <!-- Messages -->
      @for (msg of messages(); track $index) {
        @if (msg.role === 'user') {
          <div class="flex justify-end">
            <div class="max-w-[80%] rounded-lg rounded-tr-none bg-black px-3 py-2 text-white whitespace-pre-wrap break-words">
              {{ msg.content }}
            </div>
          </div>
        }
        @if (msg.role === 'assistant') {
          <div class="flex justify-start">
            <div class="max-w-[80%] rounded-lg rounded-tl-none bg-gray-100 dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
              {{ msg.content }}

              <!-- Support ticket confirmation card -->
              @if (msg.mode === 'confirm_ticket' && msg.ticket) {
                <div class="mt-2 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-3">
                  <div class="font-medium text-gray-900 dark:text-white">{{ msg.ticket.title }}</div>
                  <div class="mt-0.5 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ formatCategory(msg.ticket.category) }}</div>
                  <p class="mt-1 text-gray-700 dark:text-gray-300">{{ msg.ticket.description }}</p>
                  @if (msg.ticketStatus === 'pending') {
                    <div class="mt-3 flex space-x-2">
                      <button
                        (click)="confirmTicket($index)"
                        [disabled]="isSending()"
                        class="px-3 py-1 text-xs font-medium rounded-md bg-black text-white hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        File ticket
                      </button>
                      <button
                        (click)="dismissTicket($index)"
                        [disabled]="isSending()"
                        class="px-3 py-1 text-xs font-medium rounded-md border border-black text-black dark:border-gray-300 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Dismiss
                      </button>
                    </div>
                  } @else if (msg.ticketStatus === 'dismissed') {
                    <div class="mt-2 text-xs text-gray-400">Dismissed — not filed</div>
                  } @else {
                    <div class="mt-2 text-xs text-gray-400">Ticket filed</div>
                  }
                </div>
              }

              <!-- Navigation link -->
              @if (msg.destination) {
                <a
                  [routerLink]="getDestinationPath(msg.destination)"
                  (click)="trackDestinationClick(msg.destination)"
                  class="mt-1 block text-xs font-medium text-black dark:text-white underline underline-offset-2 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  Go to {{ msg.destination.label }}
                </a>
              }
            </div>
          </div>
        }
      }

      <!-- Typing indicator -->
      @if (isSending()) {
        <div class="flex justify-start" role="status" aria-label="Assistant is typing">
          <div class="rounded-lg rounded-tl-none bg-gray-100 dark:bg-gray-700 px-3 py-2 text-gray-400">
            <span class="inline-flex space-x-1">
              <span class="animate-bounce">.</span>
              <span class="animate-bounce [animation-delay:0.1s]">.</span>
              <span class="animate-bounce [animation-delay:0.2s]">.</span>
            </span>
          </div>
        </div>
      }
    </div>

    <!-- Input Area -->
    <div class="shrink-0 border-t border-gray-200 dark:border-gray-700 p-3 rounded-b-lg">
      <div class="flex items-center space-x-2">
        <input
          type="text"
          [ngModel]="messageText()"
          (ngModelChange)="messageText.set($event)"
          (keydown)="onKeydown($event)"
          [disabled]="isSending()"
          placeholder="Type a message..."
          aria-label="Message"
          class="flex-1 min-w-0 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-3 py-1.5 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button
          (click)="sendMessage()"
          [disabled]="isSending() || !messageText().trim()"
          class="px-3 py-1.5 text-sm font-medium rounded-md bg-black text-white hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </div>
    </div>
  </div>
}

<!-- Floating Chat Button -->
@if (!isChatOpen()) {
  <button
    (click)="toggleChat()"
    class="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-black text-white shadow-lg transition-all duration-200 hover:bg-gray-800 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
    aria-label="Open support assistant"
    title="Support assistant"
  >
    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
      <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  </button>
}
